"use client";

import * as React from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";

import "@/styles/global.css";

import type { Metadata } from "@/types/metadata";
import { appConfig } from "@/config/app";
import { getSettings as getPersistedSettings } from "@/lib/settings";
import { ThemeProvider } from "@/components/core//theme-provider";
import { Analytics } from "@/components/core/analytics";
import { AuthProvider } from "@/components/core/auth-provider";
import { I18nProvider } from "@/components/core/i18n-provider";
import { LocalizationProvider } from "@/components/core/localization-provider";
import { Rtl } from "@/components/core/rtl";
import { SettingsProvider } from "@/components/core/settings/settings-context";
import { Toaster } from "@/components/core/toaster";
import { AnimationProvider } from "@/components/core/animation-provider";
import { queryClient } from "@/lib/query-client";
import { QueryClientProvider } from "@tanstack/react-query";

const metadata = { title: appConfig.name } satisfies Metadata;

export interface RootProps {
	children: React.ReactNode;
}

export function Root({ children }: RootProps): React.JSX.Element {
	const settings = getPersistedSettings();

	return (
		<HelmetProvider>
			<Helmet>
				<title>{metadata.title}</title>
				<meta content={appConfig.themeColor} name="theme-color" />
			</Helmet>
			<>
				<Analytics>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <AnimationProvider>
                <LocalizationProvider>
                  <SettingsProvider settings={settings}>
                    <I18nProvider>
                      <Rtl>
                        <ThemeProvider>
                          {children}
                          <Toaster position="top-right" />
                        </ThemeProvider>
                      </Rtl>
                    </I18nProvider>
                  </SettingsProvider>
                </LocalizationProvider>
              </AnimationProvider>
            </AuthProvider>
          </QueryClientProvider>
				</Analytics>
			</>
		</HelmetProvider>
	);
}
